import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { fromLocal } from "helpers/fromLocal";
import {
  CalendarProjectTaskType,
  SalesTaskType,
  TechnicianAvailabilityType,
} from "operations/schema/schema";
import { asyncMutations, mutationBuilder } from "./calendar.mutations";
import { asyncQueries, queryBuilder } from "./calendar.queries";

export type CalendarViewTypes = "day" | "week" | "month";
export const calendarKeys = ["day", "week", "month"] as const;
export const CalendarViewStorageKey = "calendarSettings-view";
export const HideWeekendsStorageKey = "calendarSettings-hideWeekends";

export interface State {
  view: CalendarViewTypes;
  hideWeekends: boolean;
  selectedDate: string | null;
  mobileCalendar: {
    loading: boolean;
    availability: TechnicianAvailabilityType[];
    salesTasks: SalesTaskType[];
    projectTasks: CalendarProjectTaskType[];
    editAvailability: TechnicianAvailabilityType | null;
  };
}

interface Reducers extends SliceCaseReducers<State> {
  calendarInit: (state: State) => State;
  setView: (state: State, action: PayloadAction<{ view: CalendarViewTypes }>) => State;
  setHideWeekends: (state: State, action: PayloadAction<{ hideWeekends: boolean }>) => State;
  setCalendarSelectedDate: (state: State, action: PayloadAction<{ date: string | null }>) => State;
  setEditAvailability: (
    state: State,
    action: PayloadAction<{ availability: TechnicianAvailabilityType | null }>
  ) => State;
}

export const initialState: State = {
  view: "day",
  hideWeekends: false,
  selectedDate: null,
  mobileCalendar: {
    loading: false,
    availability: [],
    salesTasks: [],
    projectTasks: [],
    editAvailability: null,
  },
};

const reducers: Reducers = {
  calendarInit(state) {
    state.hideWeekends = fromLocal<boolean>(HideWeekendsStorageKey) ?? false;
    state.view = (localStorage.getItem(CalendarViewStorageKey) as CalendarViewTypes) ?? "day";
    return state;
  },
  setView(state, { payload: { view } }) {
    state.view = view;
    return state;
  },
  setHideWeekends(state, { payload: { hideWeekends } }) {
    state.hideWeekends = hideWeekends;
    return state;
  },
  setCalendarSelectedDate(state, { payload: { date } }) {
    state.selectedDate = date;
    return state;
  },
  setEditAvailability(state, { payload: { availability } }) {
    state.mobileCalendar.editAvailability = availability;
    return state;
  },
};

const storeBase = createSlice<State, Reducers>({
  name: "calendar",
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    queryBuilder(builder);
    mutationBuilder(builder);
  },
});

export default storeBase.reducer;
export const {
  calendarInit,
  setView,
  setHideWeekends,
  setCalendarSelectedDate,
  setEditAvailability,
} = storeBase.actions;
export const { getMobileCalendar } = asyncQueries;
export const { updateAvailability } = asyncMutations;
